import { NavCloseIcon } from 'assets/icons';
import React, { FC, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import { VoidFn } from 'types/baseTypes';

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

interface PDFPreviewProps {
  fileUrl: string;
  fileName?: string;
  onClose: VoidFn;
}

const PDFPreview: FC<PDFPreviewProps> = ({ fileUrl, fileName, onClose }) => {
  const [numPages, setNumPages] = useState<number>(0);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const onDocumentLoadSuccess = ({ numPages: totalPages }: { numPages: number }) => {
    setNumPages(totalPages);
  };

  return ReactDOM.createPortal(
    <div id="root">
      <div className="fixed inset-0 z-[999] bg-[#001324CC]" role="dialog" aria-modal="true" aria-label="PDF preview">
        <NavCloseIcon
          className="line fixed top-9 right-9 z-[1000] cursor-pointer text-primary"
          onClick={onClose}
          tabIndex={0}
          role="button"
          aria-label="Close preview"
        />
        <div className="customNormalScroll h-full w-full overflow-y-auto" onClick={handleBackdropClick}>
          <div className="mx-auto min-h-full w-full max-w-screen-lg px-4 py-10">
            <div className="flex w-full flex-col items-center justify-center gap-4">
              {fileName && <div className="mb-2 text-17px font-bold text-secondaryBtn">{fileName}</div>}
              <Document
                file={fileUrl}
                onLoadSuccess={onDocumentLoadSuccess}
                loading={<div className="text-base font-medium text-secondaryBtn">Loading file...</div>}
                error={<div className="text-base font-medium text-red-500">Failed to load file</div>}>
                {Array.from(new Array(numPages), (el, index) => (
                  <Page
                    key={`page_${index + 1}`}
                    pageNumber={index + 1}
                    renderTextLayer={true}
                    renderAnnotationLayer={true}
                    className="mb-4 shadow-lg"
                    width={Math.min(window.innerWidth * 0.8, 800)}
                  />
                ))}
              </Document>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.getElementById('portal-root')
  );
};

export default PDFPreview;
